import {
  isValidPhoneNumber,
  parsePhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';
import { z } from 'zod';

/**
 * Normalize a phone number to E.164 format.
 * Returns the original value if parsing fails (never throws).
 */
export function normalizePhoneNumber(phone: string, defaultCountry?: CountryCode): string;
export function normalizePhoneNumber(
  phone: string | null | undefined,
  defaultCountry?: CountryCode,
): string | null | undefined;
export function normalizePhoneNumber(
  phone: string | null | undefined,
  defaultCountry?: CountryCode,
): string | null | undefined {
  if (!phone) return phone;
  try {
    return parsePhoneNumber(phone, defaultCountry).number;
  } catch {
    return phone;
  }
}

export const phoneNumberSchema = z
  .string()
  .refine(
    (phoneNumber) => {
      if (!phoneNumber) {
        return true;
      }
      return isValidPhoneNumber(phoneNumber);
    },
    {
      message: 'Phone number invalid',
    },
  )
  .transform((value) => {
    // Returning null is causing the inferred type to be `string | null`
    if (!value) {
      return null;
    }
    return parsePhoneNumber(value).number;
  });

/**
 * Lenient schema for phone numbers that can be cleared (set to null).
 * This schema does NOT throw errors for invalid phone numbers - it silently
 * converts them to null to avoid blocking users.
 *
 * Behavior:
 * - Valid phone numbers → transforms to E.164 format
 * - Empty strings → null (allows clearing)
 * - null/undefined → null
 * - Invalid phone numbers → null (lenient - does not block)
 */
export const clearablePhoneNumberSchema = z
  .string()
  .nullish()
  .transform((value): string | null => {
    // Empty string, null, or undefined → null (allows clearing)
    if (!value) {
      return null;
    }

    // If it's a valid phone number, transform to E.164 format
    if (isValidPhoneNumber(value, 'US')) {
      return parsePhoneNumber(value, 'US').number;
    }

    // Invalid phone number → null (lenient behavior, does not block user)
    return null;
  });

export const requiredPhoneNumberSchema = z
  .string({ error: 'Phone number is required' })
  .min(1, 'Phone number is required')
  .refine(
    (phoneNumber) => {
      return isValidPhoneNumber(phoneNumber, 'US');
    },
    {
      message: 'Please enter a valid phone number',
    },
  )
  .transform((value) => {
    return parsePhoneNumber(value, 'US').number;
  });
